import { useRoomContext } from '@livekit/components-react';
import { SendHorizonal } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { SidePanel } from '@/components/call/SidePanel';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_LENGTH = 1000;

const timeFormat = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

export type DisplayChatMessage = {
  id: string;
  timestamp: number;
  message: string;
  from?: { identity?: string; name?: string };
};

/**
 * In-call chat. Live messages ride LiveKit; CallStage also writes them to Meet
 * so a refresh, a rejoin, or the recruiter Interviews page can still read them.
 *
 * History is owned by CallStage. The panel unmounts when closed, and a fresh
 * hook would only show what arrived after it mounted.
 */
export function ChatPanel({
  messages,
  onSend,
  isSending,
  onClose,
}: {
  messages: DisplayChatMessage[];
  onSend: (text: string) => Promise<unknown>;
  isSending: boolean;
  onClose: () => void;
}) {
  const room = useRoomContext();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pinnedToBottom = useRef(true);

  // Only auto-scroll when the reader is already at the bottom. Yanking the view
  // down while someone is reading an earlier message is worse than a missed one.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list && pinnedToBottom.current) list.scrollTop = list.scrollHeight;
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // A fixed-height textarea silently hides the second line of anything longer
  // than the box — and a pasted URL is exactly the thing people send here.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 112)}px`;
  }, [draft]);

  const submit = () => {
    const text = draft.trim();
    if (!text || isSending) return;

    setError(null);
    setDraft('');
    pinnedToBottom.current = true;

    void onSend(text).catch(() => {
      // Put the text back rather than silently losing what was typed.
      setDraft(text);
      setError('That message did not send. Check your connection and try again.');
    });
  };

  return (
    <SidePanel
      title={`Chat${messages.length > 0 ? ` · ${messages.length}` : ''}`}
      onClose={onClose}
      closeLabel="Close chat"
      error={error}
      footer={
        <form
          className="chat-form flex items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label className="sr-only-live" htmlFor="chat-input">
            Message
          </label>
          <textarea
            id="chat-input"
            ref={inputRef}
            rows={1}
            value={draft}
            maxLength={MAX_LENGTH}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — the convention every
              // chat the participants already use has trained them on.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="Send a message to everyone"
            className={cn(
              'border-input placeholder:text-muted-foreground/70 max-h-28 min-h-11 flex-1 resize-none overflow-y-auto rounded-lg border bg-white/[0.03] px-3 py-2.5 text-sm',
              'focus-visible:outline-ring/70 focus-visible:outline-2 focus-visible:outline-offset-2',
            )}
          />
          <Button
            type="submit"
            size="icon"
            aria-label="Send message"
            disabled={!draft.trim() || isSending}
            className="shrink-0"
          >
            <SendHorizonal className="size-4.5" />
          </Button>
        </form>
      }
    >
      <div
        ref={listRef}
        onScroll={(event) => {
          const el = event.currentTarget;
          pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        className="chat-log flex-1 space-y-3 overflow-y-auto p-3"
      >
        {messages.length === 0 ? (
          <p className="text-muted-foreground mt-6 px-3 text-center text-sm text-pretty">
            No messages yet. Anything sent here is visible to everyone in the call and is saved
            with the meeting.
          </p>
        ) : (
          messages.map((message) => {
            const isSelf = message.from?.identity === room.localParticipant.identity;
            const who = isSelf ? 'You' : message.from?.name || message.from?.identity || 'Someone';

            return (
              <div
                key={message.id}
                className={cn('chat-message flex flex-col gap-1', isSelf && 'items-end')}
              >
                <div className="text-muted-foreground flex items-baseline gap-2 px-1 text-[11px]">
                  <span className="chat-author max-w-[10rem] truncate font-medium">{who}</span>
                  <time dateTime={new Date(message.timestamp).toISOString()} className="tabular-nums">
                    {timeFormat.format(message.timestamp)}
                  </time>
                </div>
                <p
                  className={cn(
                    'chat-body max-w-[85%] rounded-xl px-3 py-2 text-sm break-words whitespace-pre-wrap',
                    isSelf
                      ? 'bg-primary/22 rounded-br-sm'
                      : 'border-border/60 rounded-bl-sm border bg-white/[0.04]',
                  )}
                >
                  {message.message}
                </p>
              </div>
            );
          })
        )}
      </div>
    </SidePanel>
  );
}
